import React, { useRef, useState } from 'react';
import { Direction, FileAttachment, ImageUploadErrType, ReduxState, UserData, Response } from 'interface';
import { connect, ConnectedProps } from 'react-redux';
import { Button, CircularProgress, OutlinedInput } from '@material-ui/core';
import './userdetail.style.scss';
import { API } from 'data';
import cover from '../../../../assets/images/backgrounds/emptyProfile.jpeg';
import { patch, upload } from 'scripts/api';
import { toast } from 'react-toastify';
import { emailValidation, phoneValidation } from 'scripts/validation';
import { setUserData } from 'redux/actions';
import { UploadImgErrToast } from 'data/errorCodeToast';

const UserDetail: React.FC<ConnectedProps<typeof connector>> = function (props: ConnectedProps<typeof connector>) {
    const lang = props.lang.profile.userD;
    const fileRef = useRef<any>();
    const [firstname, setFirstname] = useState<string | undefined>(props.userData?.first_name);
    const [lastname, setLastname] = useState<string | undefined>(props.userData?.last_name);
    const [email, setEmail] = useState<string | undefined>(props.userData?.email);
    const [number, setNumber] = useState<string | undefined>(props.userData?.phone);
    const [isUpload, setIsUpload] = useState<boolean>(false);
    const [updated, setUpdated] = useState(false);
    const [avatarID, setAvatarID] = useState<number | string | undefined>(undefined);
    const [avatar, setAvatar] = useState<string | undefined>(props.userData?.avatar?.avatar);

    function onCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) return;
        const FILE = e.target.files[0];
        if (!['jpg', 'jpeg', 'png', 'gif'].includes(FILE.name.split('.')[1])) {
            toast.error(lang.err400);
            return;
        }
        const ws = props.ws?.find((i) => i.is_default)?.id;
        if (!ws) return;
        setIsUpload(true);
        const form = new FormData();
        form.append('image', FILE);
        form.append('workspace', ws.toString());
        form.append('alt', FILE.name);
        upload<FileAttachment>(API.dashboard.profile.avatar, form, false)
            .then((res) => {
                setAvatarID(res.data.id);
                setAvatar(res.data.file);
                const temp = props.userData!;
                temp.avatar = { avatar: res.data.file, id: res.data.id };
                props.dispatch(setUserData(temp));
                setTimeout(() => {
                    setIsUpload(false);
                }, 2000);
            })
            .catch((res: Response<ImageUploadErrType>) => {
                if (res.status >= 500) toast.error(props.lang.serverErr);
                else if (res.status >= 400) UploadImgErrToast(res.data);
                else toast.error(props.lang.unknownErr);
            })
            .finally(() => setIsUpload(false));
    }

    function onClickHandler() {
        if (email && !emailValidation(email)) toast.error(lang.wrongEmail);
        else if (number && !phoneValidation(number)) toast.error(lang.wrongPhoneNumber);
        else {
            setUpdated(true);
            const body = {
                first_name: firstname,
                last_name: lastname,
                email: email,
                phone: number,
                avatar: avatarID,
            };
            return new Promise((resolve) =>
                patch<UserData>(API.dashboard.profile.index, body)
                    .then((res) => {
                        toast.success(lang.success);
                        props.dispatch(setUserData(res.data));
                    })
                    .catch((res) => {
                        if (res.status >= 500) toast.error(props.lang.serverErr);
                        else if (res.status >= 400) toast.error(lang.error);
                        else toast.error(props.lang.unknownErr);
                    })
                    .finally(() => {
                        setUpdated(false);
                        resolve(true);
                    }),
            );
        }
    }

    return (
        <div className="kiosk-user-detail">
            <div className="image">
                <img className="cover" src={!avatar ? cover : avatar} alt="profile_picture" />
                <input
                    disabled={isUpload}
                    type="file"
                    accept="image/*"
                    ref={fileRef}
                    onChange={onCoverUpload}
                    className="input"
                />
                {!isUpload ? (
                    <i onClick={() => fileRef.current.click()} className="material-icons edit-icon">
                        local_see
                    </i>
                ) : (
                    <CircularProgress className="edit-icon process" variant="determinate" />
                )}
            </div>
            <div className="items">
                <p>{lang.firstname}</p>
                <OutlinedInput
                    id="outlined-adornment-weight"
                    value={firstname}
                    dir={Direction.rtl}
                    onChange={(e) => setFirstname(e.target.value)}
                    endAdornment={<i className="material-icons">edit</i>}
                    className="text"
                />
            </div>
            <div className="items">
                <p>{lang.lastname}</p>
                <OutlinedInput
                    id="outlined-adornment-weight"
                    value={lastname}
                    dir={Direction.rtl}
                    onChange={(e) => setLastname(e.target.value)}
                    endAdornment={<i className="material-icons">edit</i>}
                    className="text"
                />
            </div>
            <div className="items ltr-item">
                <p>{lang.email}</p>
                <OutlinedInput
                    id="outlined-adornment-weight"
                    value={email}
                    dir={Direction.ltr}
                    onChange={(e) => setEmail(e.target.value)}
                    startAdornment={<i className="material-icons">edit</i>}
                    className="text left-align"
                />
            </div>
            <div className="items ltr-item">
                <p>{lang.number}</p>
                <OutlinedInput
                    id="outlined-adornment-weight"
                    value={number}
                    type="number"
                    dir={Direction.ltr}
                    onChange={(e) => setNumber(e.target.value)}
                    startAdornment={<i className="material-icons">edit</i>}
                    className="text"
                />
            </div>
            <div className="last-line">
                <Button onClick={onClickHandler} className="save" variant="contained" color="primary" disableElevation>
                    {!updated ? lang.save : <CircularProgress className="spinner" />}
                </Button>
            </div>
        </div>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    isAuth: state.authStatus,
    dir: state.direction,
    lang: state.lang,
    userData: state.userData,
    ws: state.workspacesData,
});

const connector = connect(mapStateToProps);
export default connector(UserDetail);
